import scrollTo from 'animated-scroll-to'
import { MuiCheckbox } from '@/modern/admin/components/mui-checkbox'
import { useAppContext } from '@/pages/_app'
import { windowStorage } from '@/utils/env'
import { Venue } from '@root/../shared/src/types/api'
import { Trans } from 'react-i18next'
import { findScrollableParent } from '@/utils/dom'
import { useCheckbox } from '@/components/checkbox'
import { useIsModernMenu } from '@/services/menu-design'

/** Ошибка согласия в новой теме — обычный текст у галочки, а не ::after:
    так её читает скринридер и с ней связано поле (aria-describedby). */
const CONSENT_ERROR_ID = 'personal-consent-error'
const CONSENT_ERROR_TEXT = 'Согласитесь на обработку персональных данных, поставив галочку'

export type PersonalDataConsentProps = {
  venue: Venue
}

export function PersonalDataConsent(props: PersonalDataConsentProps) {
  const { t } = useAppContext()
  const checkbox = useCheckbox({
    id: 'personalDataConsent',
    label: (
      <Trans
        t={t}
        i18nKey="personalDataConsent:confirm"
        values={{ venueTitle: props.venue.title }}
        components={{
          termsLink: <a className="underline" href={`/venue/${props.venue.name}/terms`} />,
        }}
      />
    ),
    // label: t('personalDataConsent:confirm', { venueTitle: props.venueTitle }),
    storage: windowStorage,
    startingValue: false,
    disabled: false,
  })
  const isModern = useIsModernMenu()
  return (
    <div className="mt-8 text-opaque dark:text-opaqueDark text-sm personal-consent-wrapper">
      <MuiCheckbox {...checkbox.props} checkboxProps={{ sx: { mb: 'auto', mt: -1 } }} />
      {/* видна, только когда проверка отметила обёртку (data-consent-error)
          и галочка не стоит — это решает CSS темы */}
      {isModern && (
        <p id={CONSENT_ERROR_ID} className="consent-err" role="alert">
          {CONSENT_ERROR_TEXT}
        </p>
      )}
    </div>
  )
}

/** Тема берётся из разметки: функция вызывается вне React-дерева. */
function isModernMenuDocument() {
  return !!document.querySelector("[data-theme='modern']")
}

export function checkPersonalDataConsent() {
  const element = document.querySelector<HTMLElement>('.personal-consent-wrapper')

  // Галочку собирают не в каждом заказе: в зале без онлайн-оплаты её на экране
  // нет. А ответ «не отмечено» остаётся в памяти браузера с прошлых заказов —
  // и проверка требовала поставить галочку, которой в этой форме не существует;
  // выйти из такого заказа гость не мог. Послабление действует только в новой
  // теме: в прежней проверка работает ровно так, как работала раньше.
  if (!element && isModernMenuDocument()) {
    return true
  }

  const userConsentedJson = windowStorage.getItem('uiState/checkbox/personalDataConsent')
  const userConsented = userConsentedJson !== null ? JSON.parse(userConsentedJson) : null

  // В новой теме согласие — только явно поставленная галочка. Раньше
  // проверка срабатывала, лишь когда галочку сняли: если её ни разу не
  // трогали, в памяти пусто, и заказ уходил без согласия (так было на
  // странице доставки). Прежнее оформление проверяет как раньше.
  const неСогласен =
    isModernMenuDocument() && element ? userConsented !== true : userConsented === false

  if (неСогласен) {
    // В новой теме — строка ошибки у самой галочки, а не системное окно:
    // окно в шторке легко пропустить, а галочка не подсвечивалась.
    if (element && isModernMenuDocument()) {
      element.setAttribute('data-consent-error', 'true')
      // галочку на странице доставки рисует общий компонент прежнего
      // оформления — строки ошибки в нём нет, добавляем её сами
      if (!element.querySelector(`#${CONSENT_ERROR_ID}`)) {
        const ошибка = document.createElement('p')
        ошибка.setAttribute('id', CONSENT_ERROR_ID)
        ошибка.setAttribute('class', 'consent-err')
        ошибка.setAttribute('role', 'alert')
        ошибка.append(CONSENT_ERROR_TEXT)
        element.appendChild(ошибка)
      }
      element
        .querySelector('input[type=checkbox]')
        ?.setAttribute('aria-describedby', CONSENT_ERROR_ID)
    } else {
      window.alert('Согласитесь на обработку персональных данных поставив галочку')
    }
    if (element) {
      // галочка может лежать в шторке заказа: прокручивать нужно её тело,
      // а не страницу под ней — иначе гость видит, как уезжает меню, а
      // галочка остаётся там же, где была. Шторка есть только в новой теме,
      // поэтому прежнее оформление прокручивается как прокручивалось
      const scrollableParent = isModernMenuDocument()
        ? findScrollableParent(element.parentElement)
        : null
      if (scrollableParent) {
        scrollTo(element, {
          verticalOffset: -100,
          elementToScroll: scrollableParent,
        })
      }
      element.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
    return false
  }

  return true
}
